import { createContext, useContext, useEffect, useSyncExternalStore } from "react";
import type { ReactNode } from "react";
import type {
  BrandingRuntime,
  BrandingRuntimeSnapshot,
} from "./branding-types";

const BrandingContext = createContext<BrandingRuntimeSnapshot | undefined>(
  undefined,
);

interface BrandingProviderProps {
  readonly runtime: BrandingRuntime;
  readonly children: ReactNode;
}

/**
 * Provide one atomically active branding snapshot and start non-blocking SOL synchronization.
 *
 * Call `runtime.bootstrap()` before mounting so the first render uses cache or compiled default.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <BrandingProvider runtime={runtime}><App /></BrandingProvider>
 * ```
 */
export function BrandingProvider({ runtime, children }: BrandingProviderProps) {
  const snapshot = useSyncExternalStore(
    runtime.subscribe,
    runtime.getSnapshot,
    runtime.getSnapshot,
  );

  useEffect(
    function synchronizeBranding() {
      runtime.resume();
      // The shell owns final disposal. Provider cleanup must stay resumable because React Strict
      // Mode intentionally performs a setup/cleanup/setup cycle before the first committed render.
      return () => runtime.pause();
    },
    [runtime],
  );

  return (
    <BrandingContext.Provider value={snapshot}>
      {children}
    </BrandingContext.Provider>
  );
}

/**
 * Read the complete active practice brand and background synchronization health.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * const { active } = useBranding();
 * ```
 */
export function useBranding(): BrandingRuntimeSnapshot {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error("useBranding must be used within BrandingProvider.");
  }
  return context;
}

/**
 * Read branding when a shell has mounted the provider, otherwise return no snapshot.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * const branding = useOptionalBranding();
 * ```
 */
export function useOptionalBranding(): BrandingRuntimeSnapshot | undefined {
  return useContext(BrandingContext);
}
